import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

export interface AppConfig {
  operation_status: boolean;
  custom_code: number;
  user_messages: any;
  validation_messages: any;
  data: {
    branch: {
      id: string;
      code: string;
      is_default: boolean;
      logo_url: string;
      cover_image_url: string;
      name: string;
      description: string;
      contacts: Array<{
        id: string;
        contact_type: "phone" | "email";
        contact_value: string;
        label: string;
        is_primary: boolean;
        sort_order: number;
      }>;
      social_media: Array<{
        id: string;
        platform: "facebook" | "instagram" | "tiktok" | "twitter" | "youtube";
        url: string;
        is_active: boolean;
        sort_order: number;
      }>;
      locations: Array<{
        id: string;
        latitude: string;
        longitude: string;
        google_maps_url: string;
        postal_code: string;
        is_primary: boolean;
        country: string;
        city: string;
        address_line_1: string;
        address_line_2: string;
        region: string;
      }>;
    };
    categories: Array<{
      id: string;
      slug: string;
      image_url: string;
      icon_url: string | null;
      is_featured: boolean;
      sort_order: number;
      name: string;
      description: string | null;
      products_count: number;
      sub_categories: Array<{
        id: string;
        slug: string;
        image_url: string | null;
        sort_order: number;
        name: string | null;
        description: string | null;
        products_count: number;
      }>;
    }>;
    pages: Array<{
      id: string;
      slug: string;
      page_type: string;
      title: string;
      content: string;
    }>;
    reviews: {
      data: Array<{
        id: string;
        customer_name: string;
        rating: number;
        review_text: string;
        review_image_url: string | null;
        type: string;
        product: any;
        approved_at: string;
        created_at: string;
      }>;
      average_rating: number;
      total_count: number;
      branch_reviews_count: number;
      product_reviews_count: number;
      distribution: Record<string, number>;
    };
    working_hours: Array<{
      day_of_week: string;
      open_time: string;
      close_time: string;
      is_closed: boolean;
    }>;
    daily_meals: Array<{
      id: string;
      slug: string;
      price: string;
      is_featured: boolean;
      calories: number;
      sort_order: number;
      name: string;
      description: string;
      primary_image: string | null;
    }>;
  };
}

export const useGetAppConfig = (options?: { enabled?: boolean }) => {
  const { i18n } = useTranslation();
  const locale = i18n.language;

  return useQuery<AppConfig>({
    queryKey: ["app-config", locale],
    queryFn: async () => {
      const response = await fetch(
        `https://back.sewargourmet.com/api/v1/app-config?locale=${locale}`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    },
    staleTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  });
};
